/**
 * rerun waitlist signup: submits an email to Klaviyo's client subscriptions API
 * without reloading the page. Markup and messages live in sections/rerun-waitlist.liquid.
 *
 * The Klaviyo public API key and list ID are section settings (theme editor → Waitlist
 * section → Klaviyo). They reach this script as data attributes; nothing to edit here.
 */

const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions';

/**
 * Klaviyo pins API behavior to a dated revision and retires old revisions after about
 * two years. Bump this when Klaviyo's changelog says 2026-07-15 is deprecated.
 */
const KLAVIYO_REVISION = '2026-07-15';

/** Shown to Klaviyo as the signup source on each profile. */
const CUSTOM_SOURCE = 'rerun waitlist page';

const REQUEST_TIMEOUT_MS = 15000;

/** Deliberately loose: catches typos like a missing @ or domain; Klaviyo does the strict check. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

class RerunWaitlistForm extends HTMLElement {
  #pending = false;

  connectedCallback() {
    this.form = this.querySelector('[data-form]');
    this.input = this.querySelector('[data-email]');
    this.button = this.querySelector('[data-submit]');
    this.buttonLabel = this.querySelector('[data-submit-label]');
    this.error = this.querySelector('[data-error]');
    this.confirmation = this.querySelector('[data-confirmation]');

    if (!this.form || !this.input || !this.button || !this.error || !this.confirmation) return;

    // The markup ships the button disabled so the form can't post anywhere before this runs.
    this.button.disabled = false;
    this.idleLabel = this.buttonLabel.textContent;

    this.form.addEventListener('submit', this.#onSubmit);
    this.input.addEventListener('input', this.#clearError);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.#onSubmit);
    this.input?.removeEventListener('input', this.#clearError);
  }

  #onSubmit = async (event) => {
    event.preventDefault();
    if (this.#pending) return;

    const email = this.input.value.trim();
    if (!EMAIL_PATTERN.test(email)) {
      this.#showError('invalid');
      return;
    }

    const { publicKey, listId } = this.dataset;
    if (!publicKey || !listId) {
      console.error('rerun waitlist: add the Klaviyo public API key and list ID in the section settings.');
      this.#showError('generic');
      return;
    }

    this.#clearError();
    this.#setPending(true);

    try {
      const response = await this.#subscribe(email, publicKey, listId);
      if (response.ok) {
        this.#showConfirmation();
      } else {
        this.#showError(await this.#errorKeyFor(response));
      }
    } catch {
      // fetch rejects on offline, DNS, CORS failures and on the timeout abort.
      this.#showError('network');
    } finally {
      this.#setPending(false);
    }
  };

  /**
   * @param {string} email
   * @param {string} publicKey
   * @param {string} listId
   */
  #subscribe(email, publicKey, listId) {
    const url = `${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(publicKey)}`;

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: CUSTOM_SOURCE,
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                },
              },
            },
          },
          relationships: {
            list: { data: { type: 'list', id: listId } },
          },
        },
      }),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
  }

  /**
   * Maps a failed Klaviyo response to one of the messages configured in the section.
   * Klaviyo answers 202 for addresses already on the list, so "already" only shows if
   * Klaviyo ever reports it as an error.
   * @param {Response} response
   * @returns {Promise<'invalid' | 'already' | 'generic'>}
   */
  async #errorKeyFor(response) {
    if (response.status === 409) return 'already';

    let errors = [];
    try {
      errors = (await response.json()).errors ?? [];
    } catch {
      // Error responses without a JSON body fall through to the generic message.
    }

    const details = errors
      .map((error) => [error.code, error.title, error.detail, error.source?.pointer].join(' '))
      .join(' ')
      .toLowerCase();

    if (/already|duplicate/.test(details)) return 'already';
    if (response.status === 400 && /email/.test(details)) return 'invalid';
    return 'generic';
  }

  /** @param {boolean} pending */
  #setPending(pending) {
    this.#pending = pending;
    this.form.setAttribute('aria-busy', String(pending));
    this.input.readOnly = pending;
    // aria-disabled instead of disabled keeps keyboard focus on the button while it waits.
    this.button.setAttribute('aria-disabled', String(pending));
    this.buttonLabel.textContent = pending ? this.dataset.pendingLabel || this.idleLabel : this.idleLabel;
  }

  /** @param {'invalid' | 'already' | 'network' | 'generic'} key */
  #showError(key) {
    const message = this.dataset[`error${key[0].toUpperCase()}${key.slice(1)}`] || this.dataset.errorGeneric;

    if (key === 'invalid') {
      this.input.setAttribute('aria-invalid', 'true');
      this.input.focus();
    }

    // Clear first so the live region re-announces a repeated message.
    this.error.textContent = '';
    requestAnimationFrame(() => {
      this.error.textContent = message;
    });
  }

  #clearError = () => {
    this.input.removeAttribute('aria-invalid');
    this.error.textContent = '';
  };

  #showConfirmation() {
    this.form.hidden = true;
    this.confirmation.hidden = false;
    // The focused control just disappeared; move focus to the message that replaced it.
    this.confirmation.focus();
  }
}

if (!customElements.get('rerun-waitlist-form')) {
  customElements.define('rerun-waitlist-form', RerunWaitlistForm);
}
